import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { cmxDateTime, commonEnums } from '@codametrix/ui-common';
import { ProviderFilters } from './providers';
import { StylesProvider, useMultiStyleConfig, useStyles, Flex, Box, Text, Spacer, List, ListItem, HStack, Divider } from '@chakra-ui/react';
import { CMXIconComponent } from '../cmx-icon/cmx-icon';
const filteredEventTypes = {
    ADT: [
        'A01',
        'A02',
        'A03',
        'A08',
        'A31'
    ]
};
const eventTypes = {
    ADT: {
        DEFAULT: 'REGISTRATION',
        A01: 'ADMIT A PATIENT',
        A02: 'TRANSFER A PATIENT',
        A03: 'DISCHARGE A PATIENT',
        A04: 'REGISTER A PATIENT',
        A05: 'PREADMIT A PATIENT',
        A06: 'TRANSFER AN OUTPATIENT TO INPATIENT',
        A07: 'TRANSFER AN INPATIENT TO OUTPATIENT',
        A08: 'UPDATE PATIENT INFORMATION',
        A09: 'PATIENT DEPARTING',
        A10: 'PATIENT ARRIVING',
        A11: 'CANCEL ADMIT',
        A12: 'CANCEL TRANSFER',
        A13: 'CANCEL DISCHARGE',
        A14: 'PENDING ADMIT',
        A15: 'PENDING TRANSFER',
        A16: 'PENDING DISCHARGE',
        A17: 'SWAP PATIENTS',
        A18: 'MERGE PATIENT INFORMATION',
        A19: 'PATIENT, QUERY',
        A20: 'NURSING/CENSUS APPLICATION UPDATES',
        A21: 'LEAVE OF ABSENCE - OUT (LEAVING)',
        A22: 'LEAVE OF ABSENCE - IN (RETURNING)',
        A23: 'DELETE A PATIENT RECORD',
        A24: 'LINK PATIENT INFORMATION',
        A25: 'CANCEL PENDING DISCHARGE',
        A26: 'CANCEL PENDING TRANSFER',
        A27: 'CANCEL PENDING ADMIT',
        A28: 'ADD PERSON INFORMATION',
        A29: 'DELETE PERSON INFORMATION',
        A30: 'MERGE PERSON INFORMATION',
        A31: 'UPDATE PERSON INFORMATION',
        A32: 'CANCEL PATIENT ARRIVING',
        A33: 'CANCEL PATIENT DEPARTING',
        A34: 'MERGE PATIENT INFORMATION - PATIENT ID ONLY',
        A35: 'MERGE PATIENT INFORMATION - ACCOUNT NUMBER ONLY',
        A36: 'MERGE PATIENT INFORMATION - PATIENT ID AND ACCOUNT NUMBER',
        A37: 'UNLINK PATIENT INFORMATION'
    },
    MDM: {
        DEFAULT: 'NOTE UPDATE',
        T02: 'NEW NOTE',
        T08: 'NOTE UPDATED',
        T11: 'NOTE CANCELED'
    },
    SIU: {
        DEFAULT: 'SURGERY UPDATE',
        S12: 'SCHEDULED SURGERY',
        S15: 'CANCELLED SURGERY'
    },
    ORM: {
        DEFAULT: 'CONSULT, OBSERVATION'
    },
    RDS: {
        DEFAULT: 'ADMINISTERED'
    },
    DFT: {
        DEFAULT: 'SUBMITTED TO BILLING'
    }
};
function MapTypetoVariant(type, match) {
    let variant;
    let timelineIcon;
    switch(type){
        case 'ADT':
        case 'Registration':
            variant = 'registration';
            timelineIcon = 'BiomeADT';
            break;
        case 'DFT':
        case 'Charge':
            variant = 'charge';
            timelineIcon = 'BiomeDFT';
            break;
        case 'SIU':
        case 'Surgery':
            variant = 'surgery';
            timelineIcon = 'BiomeSIU';
            break;
        case 'ORM':
        case 'Orders':
            variant = 'order';
            timelineIcon = 'BiomeORM';
            break;
        case 'RDS':
        case 'Medication':
            variant = 'medication';
            timelineIcon = 'BiomeRDS';
            break;
        case 'MDM':
        case 'Notes':
            variant = 'note';
            timelineIcon = 'BiomeMDM';
            break;
        default:
            variant = '';
            timelineIcon = 'BiomeCoding';
            break;
    }
    if (match) {
        variant += 'Encounter';
    }
    return {
        variant: variant,
        icon: timelineIcon
    };
}
function Message(props) {
    const { size, type, match, children, ...rest } = props;
    let typeMap = MapTypetoVariant(type, match ?? false);
    let variant = typeMap.variant;
    const styles = useMultiStyleConfig('Messages', {
        size,
        variant
    });
    return /*#__PURE__*/ _jsxs(Flex, {
        __css: styles.container,
        "aria-label": "flex-container",
        ...rest,
        children: [
            /*#__PURE__*/ _jsx(Flex, {
                __css: styles.spacing
            }),
            /*#__PURE__*/ _jsx(Flex, {
                __css: styles.contents,
                children: /*#__PURE__*/ _jsx(StylesProvider, {
                    value: styles,
                    children: children
                })
            })
        ]
    });
}
function MessageHeader(props) {
    const { children } = props;
    const styles = useStyles();
    return /*#__PURE__*/ _jsx(Flex, {
        "aria-label": "message-header",
        __css: styles.header,
        children: /*#__PURE__*/ _jsx(StylesProvider, {
            value: styles,
            children: children
        })
    });
}
function HeaderContent(props) {
    const { description, type } = props;
    let typeMap = MapTypetoVariant(type, undefined);
    let typeIcon = typeMap.icon;
    const styles = useStyles();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Flex, {
                __css: styles.type,
                children: /*#__PURE__*/ _jsx(CMXIconComponent, {
                    size: "4x",
                    variant: "light",
                    iconName: typeIcon
                })
            }),
            /*#__PURE__*/ _jsx(Box, {
                __css: styles.connector
            }),
            /*#__PURE__*/ _jsxs(HStack, {
                "aria-label": "header-content",
                __css: styles.bar,
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        sx: styles.description,
                        children: description
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        __css: styles.headSpacer
                    })
                ]
            })
        ]
    });
}
function MessageBody(props) {
    const { children } = props;
    const styles = useStyles();
    return /*#__PURE__*/ _jsx(Flex, {
        "aria-label": "message-body",
        __css: styles.body,
        ...props,
        children: /*#__PURE__*/ _jsx(List, {
            __css: styles.list,
            children: /*#__PURE__*/ _jsx(StylesProvider, {
                value: styles,
                children: children
            })
        })
    });
}
function EntryItem(props) {
    const { field, value, children } = props;
    const styles = useStyles();
    return /*#__PURE__*/ _jsxs(ListItem, {
        role: "menuitem",
        as: "text",
        __css: styles.listitem,
        children: [
            /*#__PURE__*/ _jsxs(Text, {
                as: "span",
                sx: styles.field,
                children: [
                    field,
                    ' '
                ]
            }),
            /*#__PURE__*/ _jsx(Text, {
                as: "span",
                sx: styles.value,
                children: value
            }),
            children
        ]
    });
}
function OrderCommentDivider() {
    const styles = useStyles();
    return /*#__PURE__*/ _jsx(Divider, {
        role: "separator",
        __css: styles.commentDivider
    });
}
function OrderComment(props) {
    const { field, value, children, valueStyleName } = props;
    const styles = useStyles();
    return /*#__PURE__*/ _jsxs(ListItem, {
        "aria-label": "order-comment",
        as: "text",
        __css: styles.listitem,
        children: [
            /*#__PURE__*/ _jsxs(Text, {
                as: "span",
                sx: styles.commentField,
                children: [
                    field,
                    ' '
                ]
            }),
            /*#__PURE__*/ _jsx(Text, {
                as: "span",
                sx: styles[valueStyleName],
                children: value
            }),
            children
        ]
    });
}
function MessageFooter(props) {
    const { children } = props;
    const styles = useStyles();
    return /*#__PURE__*/ _jsx(Flex, {
        "aria-label": "message-footer",
        __css: styles.footer,
        children: /*#__PURE__*/ _jsx(StylesProvider, {
            value: styles,
            children: children
        })
    });
}
function FooterContent(props) {
    const { messageDate, encounter, children } = props;
    const styles = useStyles();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Text, {
                sx: styles.datetime,
                children: messageDate
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                __css: styles.footSpacer
            }),
            /*#__PURE__*/ _jsx(Text, {
                sx: styles.encounter,
                children: encounter
            }),
            children
        ]
    });
}
const getOrderStatusFromType = (orderTypeStatus, orderType)=>{
    const orderControl = orderTypeStatus?.find((item)=>item?.['Order Control Code'] === orderType);
    return orderControl ? orderControl?.['Description'] ?? '' : '';
};
// Define a function for the common parts of the templates
const createCommonTemplate = (caseRun, entry, messageTypeGetter, messageActionGetter)=>/*#__PURE__*/ _jsx(HeaderContent, {
        type: messageTypeGetter,
        description: messageActionGetter
    });
const createFooterTemplate = (caseRun, entry, eventDatetime)=>/*#__PURE__*/ _jsx(FooterContent, {
        messageDate: eventDatetime ? cmxDateTime.format(eventDatetime || '', cmxDateTime.FORMATS.DATE_TIME_UI) : 'No date',
        encounter: entry?.encounter?.identifier ? `(${entry?.encounter.identifier})` : 'Encounter ID not available'
    });
const headerTemplates = {
    DEFAULT: (caseRun, entry)=>createCommonTemplate(caseRun, entry, commonEnums.EntriesMessageType[entry?.message?.type], convertToSentenceCaseHelper(entry?.message?.type, entry?.message?.code)),
    RDS: (caseRun, entry)=>createCommonTemplate(caseRun, entry, commonEnums.EntriesMessageType[entry?.message?.type], convertToSentenceCase(entry?.order_action ?? 'unknown')),
    ORM: (caseRun, entry, orderTypeStatus)=>{
        const diagnostic_service_category = convertToSentenceCase(entry?.diagnostic_service_category ?? 'unknown');
        const orderStatus = getOrderStatusFromType(orderTypeStatus ?? [], entry?.['order_status']);
        const orderStatusLabel = convertToSentenceCase(orderStatus ?? 'unknown');
        const headerLabel = `${diagnostic_service_category}${orderStatus ? ' (' + orderStatusLabel + ')' : ''}`;
        return createCommonTemplate(caseRun, entry, commonEnums.EntriesMessageType[entry?.message?.type], headerLabel);
    },
    MDM: (caseRun, entry)=>createCommonTemplate(caseRun, entry, commonEnums.EntriesMessageType[entry?.message?.type], convertToSentenceCaseHelper(entry?.message?.type, entry?.message?.code)),
    SIU: (caseRun, entry)=>createCommonTemplate(caseRun, entry, commonEnums.EntriesMessageType[entry?.message?.type], convertToSentenceCaseHelper(entry?.message?.type, entry?.message?.code))
};
const footerTemplates = {
    DEFAULT: (caseRun, entry)=>createFooterTemplate(caseRun, entry, entry?.meta?.event_datetime),
    RDS: (caseRun, entry)=>createFooterTemplate(caseRun, entry, entry?.meta?.event_datetime),
    ORM: (caseRun, entry)=>createFooterTemplate(caseRun, entry, entry?.meta?.event_datetime),
    MDM: (caseRun, entry)=>createFooterTemplate(caseRun, entry, entry?.note_date),
    SIU: (caseRun, entry)=>createFooterTemplate(caseRun, entry, entry?.service_date)
};
const StandardADTTemplate = (caseRun, entry)=>{
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(EntryItem, {
                field: "Facility:",
                value: na(entry?.location?.service_facility)
            }),
            /*#__PURE__*/ _jsx(EntryItem, {
                field: "Unit:",
                value: na(entry?.location?.point_of_care)
            }),
            /*#__PURE__*/ _jsx(EntryItem, {
                field: "Service:",
                value: na(entry?.hospital_service)
            }),
            /*#__PURE__*/ _jsx(EntryItem, {
                field: "Class:",
                value: na(entry?.patient_class)
            }),
            ProviderFilters.attending(entry?.providers),
            ProviderFilters.admitting(entry?.providers),
            /*#__PURE__*/ _jsx(EntryItem, {
                field: "Insurance:",
                value: entry?.insurances?.map((insurance)=>insurance.company_name).join(', ')
            })
        ]
    });
};
const bodyADTTemplate = {
    DEFAULT: StandardADTTemplate,
    A01: StandardADTTemplate,
    A02: StandardADTTemplate,
    A03: StandardADTTemplate,
    A08: StandardADTTemplate,
    A31: StandardADTTemplate
};
const StandardSIUTemplate = (caseRun, entry)=>{
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(EntryItem, {
                field: "Specialty:",
                value: na(entry?.surgical_specialty)
            }),
            /*#__PURE__*/ _jsx(EntryItem, {
                field: "Description:",
                value: na(entry?.description)
            }),
            ProviderFilters.primarySurgeon(entry?.providers)
        ]
    });
};
const bodySIUTemplate = {
    DEFAULT: StandardSIUTemplate,
    S12: StandardSIUTemplate,
    S15: StandardSIUTemplate
};
const StandardMDMTemplate = (caseRun, entry)=>{
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(EntryItem, {
                field: "Type:",
                value: na(entry?.note_type)
            }),
            /*#__PURE__*/ _jsx(EntryItem, {
                field: "Specialty:",
                value: na(entry?.note_specialty)
            }),
            ProviderFilters.authoring(entry?.providers),
            ProviderFilters.finalSignProvider(entry?.providers)
        ]
    });
};
const bodyMDMTemplate = {
    DEFAULT: StandardMDMTemplate,
    T02: StandardMDMTemplate,
    T08: StandardMDMTemplate,
    T11: StandardMDMTemplate
};
const domParser = new DOMParser();
const buildOrderComment = (htmlString, orderNoteID, orderNoteCategory)=>{
    let textContent = domParser.parseFromString(htmlString, 'text/html').body.textContent;
    if (textContent) {
        let textContentArray = textContent.split('->');
        let name = textContentArray[0] + (textContentArray[0].slice(-1) === '?' || textContentArray[0].slice(-1) === ':' ? '' : ':');
        let value = textContentArray[1];
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ _jsx(OrderComment, {
                field: name,
                value: value,
                valueStyleName: orderNoteCategory === 'Admission' && textContent.includes('Admitting Provider') ? 'value' : 'commentValue'
            }, orderNoteID)
        });
    } else {
        return '';
    }
};
const bodyORMTemplate = {
    DEFAULT: (caseRun, entry)=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(EntryItem, {
                    field: "Specialty:",
                    value: na(entry?.diagnostic_serv_sect_id)
                }),
                ProviderFilters.entering(entry?.providers),
                ProviderFilters.ordering(entry?.providers),
                ProviderFilters.admitting(entry?.providers),
                ProviderFilters.cosigning(entry?.providers),
                ProviderFilters.attending(entry?.providers),
                entry?.order_notes && entry.order_notes.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(OrderCommentDivider, {}),
                        entry.order_notes.map((order_note)=>buildOrderComment(order_note?.comment, order_note.id, entry.diagnostic_service_category))
                    ]
                })
            ]
        });
    }
};
const bodyRDSTemplate = {
    DEFAULT: (caseRun, entry)=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(EntryItem, {
                    field: "Medication:",
                    value: na(entry.medication_administered)
                }),
                ProviderFilters.medicationOrdering(entry?.providers)
            ]
        });
    }
};
const bodyDFTTemplate = {
    DEFAULT: (caseRun, entry)=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                ProviderFilters.billing(entry?.providers),
                /*#__PURE__*/ _jsx(EntryItem, {
                    field: "Codes:",
                    value: entry?.procedures?.join(', ')
                })
            ]
        });
    }
};
const resolveBodyTemplate = (activeCaseRun, entry)=>{
    const messageType = entry.message?.type;
    const messageCode = entry.message?.code;
    const templates = {
        ADT: bodyADTTemplate,
        SIU: bodySIUTemplate,
        MDM: bodyMDMTemplate,
        ORM: bodyORMTemplate,
        RDS: bodyRDSTemplate,
        DFT: bodyDFTTemplate
    };
    if (messageType && messageCode && templates[messageType]) {
        const templateFn = templates[messageType][messageCode] || templates[messageType]['DEFAULT'];
        if (typeof templateFn === 'function') {
            return templateFn(activeCaseRun, entry);
        }
    }
    return null;
};
const resolveHeadTemplate = (activeCaseRun, entry, orderTypeStatus)=>{
    if (typeof headerTemplates[entry?.message?.type] === 'function') {
        return headerTemplates[entry?.message?.type](activeCaseRun, entry, orderTypeStatus);
    } else if (typeof headerTemplates['DEFAULT'] === 'function') {
        return headerTemplates['DEFAULT'](activeCaseRun, entry, orderTypeStatus);
    }
    return null;
};
const resolveFootTemplate = (activeCaseRun, entry)=>{
    if (typeof footerTemplates[entry?.message?.type] === 'function') {
        return footerTemplates[entry?.message?.type](activeCaseRun, entry);
    } else if (typeof footerTemplates['DEFAULT'] === 'function') {
        return footerTemplates['DEFAULT'](activeCaseRun, entry);
    }
    return null;
};
const convertToSentenceCaseHelper = (messageType, messageCode)=>{
    if (!messageType || !messageCode) {
        return '';
    }
    const messageTypeData = eventTypes?.[messageType];
    const messageCodeData = messageTypeData?.[messageCode] || messageTypeData?.DEFAULT || '';
    return convertToSentenceCase(messageCodeData);
};
const convertToSentenceCase = (initialText)=>{
    if (initialText) {
        const converted = initialText?.toLowerCase();
        return converted?.charAt(0).toUpperCase() + converted.slice(1);
    }
    return '';
};
const na = (initial)=>{
    return typeof initial !== 'object' ? initial ?? 'N/A' : '';
};
const isInteractionFromCase = (entry, activeCaseRun)=>{
    if (entry?.encounter?.type === 'HAR' && entry?.encounter?.identifier && activeCaseRun?.encounter_identifier?.number) {
        return entry?.encounter?.identifier === activeCaseRun?.encounter_identifier?.number;
    }
    if (entry?.encounter?.type === 'CSN' && entry?.encounter?.identifier && activeCaseRun?.visit_number?.number) {
        return entry?.encounter?.identifier === activeCaseRun?.visit_number?.number;
    }
    return false;
};
export { eventTypes, filteredEventTypes, resolveBodyTemplate, resolveHeadTemplate, resolveFootTemplate, isInteractionFromCase, EntryItem, Message, MessageHeader, MessageBody, MessageFooter };
